import type { OverlayTriggerState } from '@react-stately/overlays';
import { useQuery } from '@tanstack/react-query';
import { Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { Check, Instagram, StrongPhone, Whatsapp, ZoobinLogo } from '@zoomit/icons';
import { getCookie, Image, Link, Modal, refreshSession, Z_SESSION_COOKIE_KEY } from '@zoomit/primitives';
import { Button, ModalActions, ModalHead, Typography } from '@zoomit/zoomit-ds';

import { getPhysicalProductDetailsByClick } from '../../apis/product';
import { mapLinkHandler } from '../../helpers/map-link-handler';
import type { UseHistoryStateReturnType } from '../../hooks/useHistoryState';
import { useOrigin } from '../../providers/OriginProvider';
import { GlobalLoading, Score } from '../shared';

type Props = {
  openContactInfo: UseHistoryStateReturnType<OverlayTriggerState>;
  id: string;
  storeId: string;
  isHybridStore: boolean;
};

export const PhysicalStoreListItemModal = (props: Props) => {
  const { openContactInfo, id, storeId, isHybridStore } = props;

  const { data, isLoading } = useQuery({
    queryKey: ['storeContactInfo'],
    queryFn: async () => {
      try {
        let sessionId = getCookie(Z_SESSION_COOKIE_KEY);
        let attempts = 0;
        const maxAttempts = 10;

        while (!sessionId && attempts < maxAttempts) {
          refreshSession();
          sessionId = getCookie(Z_SESSION_COOKIE_KEY);
          attempts++;
        }

        if (!sessionId) {
          throw new Error('Failed to obtain session after maximum attempts');
        }

        const res = await getPhysicalProductDetailsByClick({ id, storeId, sessionId, isHybridStore });
        return res.data;
      } catch (error) {
        console.error('Error fetching store contact info:', error);
        throw error;
      }
    },
    enabled: openContactInfo.isOpen,
  });

  const handleNavigation = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    mapLinkHandler({
      latitude: String(data?.address?.location?.latitude),
      longitude: String(data?.address?.location?.longitude),
      label: data?.name ?? '',
    });
  };

  const { baseMediaUrl } = useOrigin();

  if (isLoading) {
    return <GlobalLoading Logo={ZoobinLogo} />;
  }

  return (
    <Modal containerClassName="flex flex-col h-full md:justify-center md:max-h-[50vh]">
      <ModalHead title="جزئیات فروشگاه" onClose={openContactInfo.close} />
      <div className="bg-background1 border-borderPrimary flex-[1] border-b p-6">
        <div className="h-[301px] overflow-hidden">
          <Swiper
            pagination={{
              bulletActiveClass: 'bg-primary1',
              dynamicMainBullets: 3,
            }}
            dir="rtl"
            modules={[Pagination]}
          >
            {data?.images?.map((image) => (
              <SwiperSlide key={image.id}>
                <Image
                  src={`${baseMediaUrl}/${image.id}`}
                  width={452}
                  height={301}
                  alt={image.alt || 'physical store image'}
                  className="h-[301px] rounded-md object-cover"
                  placeholder="blur"
                  blurDataURL={image.preview}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
        <div className="mt-6 flex items-center gap-3">
          <Typography fontSize={1.6} fontWeight={700}>
            {data?.name}
          </Typography>
          {!!data?.userReviewsInfo.averageScore && (
            <Score
              className="gap-2"
              score={data?.userReviewsInfo.averageScore}
              reviewCounts={data?.userReviewsInfo.usersCount}
            />
          )}
        </div>
        <div className="mt-2">
          <Typography className="fa text-grey1" fontSize={1.6} lineHeight={1.8}>
            {data?.description}
          </Typography>
          <Typography className="fa text-grey1" fontSize={1.6} lineHeight={1.8}>
            {data?.address?.description}
          </Typography>
        </div>
        <div>
          {data?.hasSenfiPhysicalNumber && (
            <div className="bg-borderPrimary text-subhead my-4 flex w-fit items-center justify-center gap-2 rounded-full px-2 py-1">
              <Check stroke="#0FA862" size="md" />
              <Typography className="fa text-subhead leading-none" fontWeight={400}>
                جواز کسب
              </Typography>
            </div>
          )}
        </div>
        <div>
          {data?.address?.phoneNumbers.length && (
            <div className="border-borderPrimary flex items-center justify-between gap-2 border-b border-dashed py-2">
              <div className="flex items-center gap-2">
                <StrongPhone />
                <Typography className="fa text-grey1" fontSize={1.4} fontWeight={400} lineHeight={2}>
                  شماره تلفن
                </Typography>
              </div>
              <div>
                {data?.address?.phoneNumbers.map((item, index, list) => (
                  <Link href={`tel:${item}`} key={index}>
                    <Typography
                      className="text-text text-body hover:text-primary2 fa cursor-pointer"
                      fontWeight={700}
                    >
                      {item}
                      {index !== list.length - 1 && ', '}
                    </Typography>
                  </Link>
                ))}
              </div>
            </div>
          )}
          {data?.whatsAppPhoneNumber && (
            <div className="border-borderPrimary flex items-center justify-between gap-2 border-b border-dashed py-2">
              <div className="flex items-center gap-2">
                <Whatsapp />
                <Typography className="fa text-grey1" fontSize={1.4} fontWeight={400} lineHeight={2}>
                  واتساپ
                </Typography>
              </div>
              <Link href={`https://wa.me/${data?.whatsAppPhoneNumber}`} target="_blank">
                <Typography
                  className="text-text text-body hover:text-primary2 fa cursor-pointer"
                  fontWeight={700}
                >
                  {data?.whatsAppPhoneNumber}
                </Typography>
              </Link>
            </div>
          )}
          {data?.instagramUrl && (
            <div className="border-borderPrimary flex items-center justify-between gap-2 border-b border-dashed py-2">
              <div className="flex items-center gap-2">
                <Instagram />
                <Typography className="fa text-grey1" fontSize={1.4} fontWeight={400} lineHeight={2}>
                  اینستاگرام
                </Typography>
              </div>
              <div>
                <Link href={data?.instagramUrl} target="_blank">
                  <Typography
                    className="text-text text-body hover:text-primary2 fa cursor-pointer"
                    fontWeight={700}
                  >
                    {data?.instagramUrl}
                  </Typography>
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
      <ModalActions className="flex flex-nowrap gap-4">
        <Button className="flex-1/2" onClick={handleNavigation}>
          مسیریابی
        </Button>
        <Button className="flex-1/2" variant="outline" onClick={openContactInfo.close}>
          بستن
        </Button>
      </ModalActions>
    </Modal>
  );
};
